/* eslint-disable max-len */

/* eslint-disable react/no-danger */

/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import { Grid, Link, useMediaQuery, useTheme } from '@material-ui/core';
import { Delete, List } from '@material-ui/icons';
import { CSSProperties } from '@material-ui/styles';
import { Button, IconButton, Panel, useDialogs } from '@michelin/acid-components';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { getOptionLabel } from '@michelin/select-options-provider';
import Comments from 'components/Comments';
import ButtonPopover from 'components/Comments/ButtonPopover';
import { DataGrid } from 'devextreme-react';
import { Column, Scrolling } from 'devextreme-react/data-grid';
import { useFieldArray, useFormContext } from 'react-hook-form';
import SOPCell from '../SOPCell';
import TireProfileQuickView from '../TireProfileQuickView';
import { Actions, SOPProfile, SOPTireProfile, SOPWheelBalance as SOPWheelBalanceType } from '../Types';
import { saveUseFieldArray } from '../utils';
import { WheelBalanceFormModal } from './form';

interface SOPWheelBalanceProps {
  action: Actions;
  modalState: { action: Actions; open: boolean };
  handleModalState: (action: Actions, open: boolean) => void;
  tireProfile: SOPTireProfile[] | null;
}

function SOPWheelBalance(props: SOPWheelBalanceProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { control } = useFormContext<SOPProfile>();
  const { confirmDialog } = useDialogs();
  const theme = useTheme();
  const cellView = useMediaQuery(theme.breakpoints.down(500));

  const { fields, append, update, remove } = useFieldArray<SOPProfile, 'wheel_balance.wheel_balances', 'key'>({
    control,
    name: 'wheel_balance.wheel_balances',
    keyName: 'key',
  });

  const showWarning = fields.filter((i) => (i.tire_profile_errors || []).length > 0).length > 0;
  const [index, setIndex] = useState<number>(fields.length);
  const style: CSSProperties = {
    color: theme.palette.primary.main,
    fontSize: '1em',
  };

  function RenderId(id: any, colIndex: number) {
    const idFormat = (id || 0).toString().padStart(5, 'WB000');
    if (!permissions.allowsAction('sop.update')) return <div>{idFormat}</div>;

    return (
      <Link
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setIndex(colIndex);
          props.handleModalState(props.action === 'view' ? 'view' : 'edit', true);
        }}
      >
        {idFormat}
      </Link>
    );
  }

  function RenderUom(field: number, uom: string) {
    return <span>{`${field} ${getOptionLabel('weight_types', uom)}`}</span>;
  }

  function ExtraInfo(data: any) {
    return (
      data?.preferred_rotation_pattern ||
      data?.balancing_preference ||
      data?.require_radial_runout_match_mounting ||
      data?.max_weight ||
      data.comments
    );
  }

  function RenderExtraInfo(data: any) {
    const headerStyle: CSSProperties = { fontWeight: 'bolder' };
    const rotationPattern = getOptionLabel('preferred_tire_rotation_patterns', data?.preferred_rotation_pattern);
    const balancing = getOptionLabel('balance_options', data?.balancing_preference);
    const matchMounting = data.require_radial_runout_match_mounting ? t('Yes') : t('No');

    return (
      <Grid container spacing={0}>
        <Grid item xs={12} style={headerStyle}>
          {t('Render Preferences')}
        </Grid>
        <Grid item container xs={12}>
          {rotationPattern && (
            <Grid item xs={12}>
              <span style={style}>{t('Rotation Pattern')}</span>
              {`: ${rotationPattern}`}
            </Grid>
          )}
          {balancing && (
            <Grid item xs={12}>
              <span style={style}>{t('Balancing')}</span>
              {`: ${balancing}`}
            </Grid>
          )}
          <Grid item xs={12}>
            <span style={style}>{t('Radial Runout Match Mounting')}</span>
            {`: ${matchMounting}`}
          </Grid>
        </Grid>
        {data.max_weight !== 0 && data.max_weight && (
          <Grid item xs={12}>
            <span style={headerStyle}>{t('Max Weight')}</span>:{RenderUom(data.max_weight, data.uom)}
          </Grid>
        )}
        {data.comments && (
          <Grid item xs={12}>
            <span style={headerStyle}>{t('Comments')}</span>
            {`: ${data.comments}`}
          </Grid>
        )}
      </Grid>
    );
  }

  function ActionsCell({ i, data }: { i: number; data: any }) {
    const showDelete = permissions.allowsAction('sop.update') && !(props.action === 'view');

    return (
      <div style={{ minWidth: '120px' }}>
        {ExtraInfo(data) && <Comments comments={RenderExtraInfo(data)} icon={<List />} />}
        {showDelete && (
          <IconButton
            aria-label="delete"
            color="primary"
            onClick={() => {
              confirmDialog(
                t('Are you sure you want to delete this Wheel Balancing?'),
                t('Delete Wheel Balancing'),
                t('Yes'),
                t('No'),
                () => {
                  remove(i);
                },
              );
            }}
            size="small"
          >
            <Delete />
          </IconButton>
        )}
      </div>
    );
  }

  const ActionButton = () => {
    if (props.action === 'view') return <></>;
    return (
      <>
        <ButtonPopover text={t('Tire Profile')} detail={<TireProfileQuickView tireProfile={props.tireProfile} />} />
        <Button size="small" color="primary" onClick={() => props.handleModalState('add', true)}>
          {t('Add')}
        </Button>
      </>
    );
  };

  const WheelBalanceGrid = () => (
    <Grid container spacing={2} direction="row">
      {showWarning && (
        <Grid item xs={12}>
          <div
            style={{ textAlign: 'center', color: '#f99738' }}
            dangerouslySetInnerHTML={{
              __html: t(
                '<b style="color:#eb8828">Instructions:</b> Please mouse over each item shown below <b style="color:#f16f6f">in RED font</b> for specific instructions on how to correct the data mismatch issues between the linked Onsite Tire Profile and this SOP Profile.',
              ),
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <DataGrid
          dataSource={fields}
          className="freespaced-table"
          showBorders
          wordWrapEnabled
          columnAutoWidth
          style={{ maxHeight: `calc(100vh - ${showWarning ? '400px' : '315px'})` }}
        >
          <Scrolling mode="virtual" />
          <Column
            caption={t('Id')}
            dataField="id"
            allowSorting
            alignment="center"
            cellRender={(column: any) => RenderId(column.value, fields.indexOf(column.data))}
          />
          <Column
            caption={t('Axle Position')}
            dataField="axle_type"
            allowSorting
            alignment="center"
            cellRender={(column: any) => (
              <SOPCell
                values={[column.value]}
                errors={column.data.tire_profile_errors}
                type="axle"
                tireProfile={props.tireProfile}
              />
            )}
          />
          <Column
            caption={t('Application Type')}
            dataField="application_type"
            allowSorting
            alignment="center"
            cellRender={(column: any) => (
              <SOPCell
                values={[column.value]}
                errors={column.data.tire_profile_errors}
                type="application"
                tireProfile={props.tireProfile}
              />
            )}
          />
          <Column
            caption={t('Vehicle Type')}
            dataType="string"
            dataField="vehicle_type"
            allowSorting
            alignment="left"
            cellRender={(column: any) => (
              <SOPCell
                values={[column.value]}
                errors={column.data.tire_profile_errors}
                type="vehicle"
                tireProfile={props.tireProfile}
              />
            )}
          />
          <Column
            allowSearch={false}
            allowSorting={false}
            cellRender={(row) => <ActionsCell i={fields.indexOf(row.data)} data={row.data} />}
          />
        </DataGrid>
      </Grid>
    </Grid>
  );

  return (
    <Panel title={cellView ? '' : t('Wheel Balancing')} control={<ActionButton />}>
      <Grid container spacing={2} direction="row">
        <WheelBalanceGrid />
        <WheelBalanceFormModal
          action={props.modalState.action}
          open={props.modalState.open}
          data={(props.modalState.action === 'add' ? {} : fields[index]) as SOPWheelBalanceType}
          list={fields}
          tireProfile={props.tireProfile}
          onSave={(e: SOPWheelBalanceType) => {
            delete e.tire_profile_errors;
            if (props.modalState.action === 'add') saveUseFieldArray(e, props.tireProfile, fields, append);
            if (props.modalState.action === 'edit') update(index, e);
            // Refresh the action and form state
            props.handleModalState('', false);
          }}
          handleOpen={(value: boolean) => props.handleModalState('', value)}
        />
      </Grid>
    </Panel>
  );
}

export default SOPWheelBalance;
